import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PublishIcon from "@mui/icons-material/Publish";

import "../../settings.styles.css";
import "../../company/menus/menu.settings.styles.css";

import DeleteModal from "../../../../Components/Common/DeleteModal";
import SettingsPanel from "../../components/SettingsPanel";
import MenuListPanel from "../../company/menus/components/MenuListPanel";
import MenuTreeBuilder from "../../company/menus/components/MenuTreeBuilder";

import {
  createMasterMenuNav,
  createMasterMenuNavSuccess,
  deleteMasterMenuNav,
  deleteMasterMenuNavSuccess,
  getAvailableMenuNavigationItems,
  getMasterMenuNavData,
  getMasterMenuNavList,
  rolloutMasterMenuNav,
  rolloutMasterMenuNavSuccess,
  updateMasterMenuNav,
  updateMasterMenuNavSuccess,
} from "../../../../store/navigationMenu/action";

/**
 * Global Menus (Super Admin) — CRMit Task 24886.
 *
 * The same builder as Settings > Menus, pointed at ZCRM_Master. A menu built
 * here is a DEFINITION: it does nothing in any company until it is rolled out,
 * which is a deliberate, visible step rather than a save-time side effect —
 * saving a half-finished menu straight into 100+ live sidebars is not a thing
 * you want one keystroke away.
 *
 * The destinations come from the same catalogue as the company builder
 * (/menunavoption/list), which is already master-level, so a global menu can
 * only ever point at screens that exist everywhere.
 */
const GlobalMenuSettings = () => {
  const dispatch = useDispatch();

  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [rolloutResult, setRolloutResult] = useState(null);

  const {
    masterMenuNavListAPI,
    masterMenuNavDataAPI,
    availableMenuItemsAPI,
    rolloutLoading,
    createSuccessAPI,
    updateSuccessAPI,
    deleteSuccessAPI,
    rolloutSuccessAPI,
  } = useSelector((state) => ({
    masterMenuNavListAPI:
      state.NavigationMenu.masterMenuNavList?.mastermenunav_list,
    masterMenuNavDataAPI: state.NavigationMenu.masterMenuNavData,
    // The destination catalogue lives in ZCRM_Master already, so the company
    // builder's own list is the right source here too.
    availableMenuItemsAPI:
      state.NavigationMenu.availableMenuNavigationItems?.menunavoption_list,
    rolloutLoading: state.NavigationMenu.rolloutMasterMenuNavLoading,
    createSuccessAPI: state.NavigationMenu.createMasterMenuNavSuccess,
    updateSuccessAPI: state.NavigationMenu.updateMasterMenuNavSuccess,
    deleteSuccessAPI: state.NavigationMenu.deleteMasterMenuNavSuccess,
    rolloutSuccessAPI: state.NavigationMenu.rolloutMasterMenuNavSuccess,
  }));

  useEffect(() => {
    dispatch(getMasterMenuNavList());
    dispatch(getAvailableMenuNavigationItems());
  }, [dispatch]);

  const menus = useMemo(
    () => [...(masterMenuNavListAPI || [])],
    [masterMenuNavListAPI]
  );

  // The catalogue rows are shaped for the builder, which expects the company
  // item shape (Id / Name / Icon / Url). MenuNavOption.Id IS the id a master
  // structure row stores, so no mapping table is needed.
  const availableItems = useMemo(
    () =>
      (availableMenuItemsAPI || []).map((row) => ({
        Id: row.Id,
        Name: row.Name,
        Icon: row.Icon,
        Url: row.UrlReact || row.Url,
        MenuNavOption_Id: row.Id,
        FlagPlaceholder: 0,
      })),
    [availableMenuItemsAPI]
  );

  const refreshList = () => dispatch(getMasterMenuNavList());

  const handleSelectMenu = (id) => {
    setSelectedId(String(id));
    setRolloutResult(null);
    dispatch(getMasterMenuNavData({ id }));
  };

  const selectedMenuData =
    masterMenuNavDataAPI?.mastermenunav_data &&
    String(masterMenuNavDataAPI.mastermenunav_data.Id) === String(selectedId)
      ? masterMenuNavDataAPI.mastermenunav_data
      : null;
  const selectedMenuList = selectedMenuData
    ? masterMenuNavDataAPI?.menunavstructure_list || []
    : [];
  const selectedMenuRow = menus.find(
    (m) => String(m.Id) === String(selectedId)
  );

  // ---- create ----
  const handleCreateMenu = (name) => {
    dispatch(createMasterMenuNav([{ Name: name, menunavstructure_list: [] }]));
  };
  useEffect(() => {
    if (isEmpty(createSuccessAPI)) return;
    if (createSuccessAPI.status === 200) {
      toast.success(createSuccessAPI.message);
      refreshList();
      if (createSuccessAPI.MasterMenuNav_Id) {
        handleSelectMenu(createSuccessAPI.MasterMenuNav_Id);
      }
    } else {
      toast.error(createSuccessAPI.message);
    }
    dispatch(createMasterMenuNavSuccess([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createSuccessAPI]);

  // ---- save structure ----
  const handleSaveMenu = (structure, name) => {
    setSaving(true);
    dispatch(
      updateMasterMenuNav([
        { Id: selectedId, Name: name, menunavstructure_list: structure },
      ])
    );
  };
  useEffect(() => {
    if (isEmpty(updateSuccessAPI)) return;
    setSaving(false);
    if (updateSuccessAPI.status === 200) {
      // Deliberately not just "Saved": the change is inert until rollout, and
      // that is the single most misreadable thing about this screen.
      toast.success("Saved. Roll out to push this to company databases.");
      if (selectedId) dispatch(getMasterMenuNavData({ id: selectedId }));
      refreshList();
    } else {
      toast.error(updateSuccessAPI.message);
    }
    dispatch(updateMasterMenuNavSuccess([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSuccessAPI]);

  // ---- delete ----
  const askDeleteMenu = (id) => {
    setPendingDeleteId(id);
    setDeleteModal(true);
  };
  const confirmDeleteMenu = () => {
    dispatch(deleteMasterMenuNav([pendingDeleteId]));
  };
  useEffect(() => {
    if (isEmpty(deleteSuccessAPI)) return;
    if (deleteSuccessAPI.status === 200) {
      toast.success(deleteSuccessAPI.message);
      if (String(pendingDeleteId) === String(selectedId)) setSelectedId(null);
      refreshList();
    } else {
      toast.error(deleteSuccessAPI.message);
    }
    setDeleteModal(false);
    setPendingDeleteId(null);
    dispatch(deleteMasterMenuNavSuccess([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteSuccessAPI]);

  // ---- rollout ----
  const handleRollout = () => {
    setRolloutResult(null);
    dispatch(rolloutMasterMenuNav([{ Id: selectedId }]));
  };
  useEffect(() => {
    if (isEmpty(rolloutSuccessAPI)) return;
    if (rolloutSuccessAPI.status === 200) {
      toast.success(rolloutSuccessAPI.message);
      // Kept on screen rather than only toasted: with 100+ databases, "which
      // ones failed" is the whole answer, and a toast is gone in four seconds.
      setRolloutResult(rolloutSuccessAPI);
      refreshList();
    } else {
      toast.error(rolloutSuccessAPI.message);
    }
    dispatch(rolloutMasterMenuNavSuccess([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolloutSuccessAPI]);

  const failedRollouts = (rolloutResult?.rollout_list || []).filter(
    (row) => Number(row.FlagSuccess) !== 1
  );

  return (
    <>
      {deleteModal && (
        <DeleteModal
          show={deleteModal}
          onDeleteClick={confirmDeleteMenu}
          onCloseClick={() => {
            setDeleteModal(false);
            setPendingDeleteId(null);
          }}
        />
      )}

      <SettingsPanel
        title="Global Menus"
        description="Menus CRMit defines once and rolls out to every company database"
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          A menu here is a definition. Nothing changes in a company until you
          roll it out. Rolled-out menus appear in that company&apos;s Menus
          settings marked <strong>GLOBAL</strong> and cannot be edited there —
          the company clones one to get a copy of its own, and that copy is
          never touched by a later rollout.
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4} lg={3}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <MenuListPanel
                menus={menus}
                selectedId={selectedId}
                onSelect={handleSelectMenu}
                onCreate={handleCreateMenu}
                onDelete={askDeleteMenu}
                title="Global menus"
                createPlaceholder="New global menu name"
                getSubtitle={(menu) =>
                  menu.QtyCompanyTotal
                    ? `Rolled out to ${menu.QtyCompanyRolledOut || 0} of ${
                        menu.QtyCompanyTotal
                      }`
                    : null
                }
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={8} lg={9}>
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, minHeight: 300 }}
            >
              {selectedMenuData ? (
                <>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ sm: "center" }}
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        color="info"
                        variant="outlined"
                        label="GLOBAL"
                      />
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {selectedMenuRow?.QtyCompanyTotal
                          ? `In ${selectedMenuRow.QtyCompanyRolledOut || 0} of ${
                              selectedMenuRow.QtyCompanyTotal
                            } company databases`
                          : "Not rolled out yet"}
                      </Typography>
                    </Stack>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PublishIcon />}
                      onClick={handleRollout}
                      disabled={rolloutLoading}
                    >
                      {rolloutLoading
                        ? "Rolling out…"
                        : "Roll out to all companies"}
                    </Button>
                  </Stack>

                  {rolloutLoading && <LinearProgress sx={{ mb: 2 }} />}

                  {rolloutResult && (
                    <Alert
                      severity={
                        Number(rolloutResult.QtyFailed) > 0 ? "warning" : "success"
                      }
                      sx={{ mb: 2 }}
                      onClose={() => setRolloutResult(null)}
                    >
                      {rolloutResult.message}
                      {failedRollouts.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Company</TableCell>
                                <TableCell>Database</TableCell>
                                <TableCell>Why it failed</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {failedRollouts.map((row) => (
                                <TableRow key={row.DatabaseName}>
                                  <TableCell>{row.Company_Name}</TableCell>
                                  <TableCell>{row.DatabaseName}</TableCell>
                                  <TableCell>{row.Message}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      )}
                    </Alert>
                  )}

                  <MenuTreeBuilder
                    key={selectedId}
                    menuData={selectedMenuData}
                    initialList={selectedMenuList}
                    availableItemsAll={availableItems}
                    saving={saving}
                    onSave={handleSaveMenu}
                    /* A global menu is duplicated as another GLOBAL menu, so
                       this stays on the master create path. */
                    onDuplicate={(name, structure) =>
                      dispatch(
                        createMasterMenuNav([
                          { Name: name, menunavstructure_list: structure },
                        ])
                      )
                    }
                    /* The destination catalogue is edited on Super Menu, not
                       here — passing no handlers keeps those controls off. */
                    onCreateItem={undefined}
                    onEditItem={undefined}
                    onDeleteAvailableItem={undefined}
                  />
                </>
              ) : (
                <Box
                  sx={{
                    height: 260,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "text.disabled",
                  }}
                >
                  <i
                    className="fa-solid fa-earth-europe"
                    style={{ fontSize: 32, marginBottom: 12 }}
                  />
                  <Typography variant="body1">
                    Select a global menu on the left, or create one to start
                    building.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ mt: 3 }} />
      </SettingsPanel>
    </>
  );
};

export default GlobalMenuSettings;
